import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Rocket, 
  HelpCircle, 
  Settings, 
  Mic, 
  Volume2, 
  Shield, 
  UserCheck, 
  Zap, 
  Wifi, 
  WifiOff, 
  Sparkles, 
  ChevronRight, 
  Compass, 
  Gauge, 
  FileText, 
  X, 
  Search, 
  Tag, 
  Bot, 
  Grid, 
  MessageSquare, 
  AlertTriangle, 
  Info, 
  CheckCircle,
  Clock,
  Briefcase,
  Layers,
  PhoneCall,
  Download,
  Check,
  Eye,
  Activity,
  UserPlus
} from 'lucide-react';
import { 
  OnboardingState, 
  DEMO_MAMAS_KITCHEN_VENTURE, 
  DEMO_DISCOVERY_KAMPALA, 
  DEMO_OPTIMIZE_BUSINESS, 
  DEMO_SCRUTINY_ANALYSIS, 
  OfflineStorageManager, 
  VentureFuzzySearch, 
  InAppAlertEngine, 
  CustomerSupportEngine, 
  ComplianceConsentCenter, 
  ExperimentEngine, 
  FoundersDirectNetwork, 
  CostAndCircuitBreaker, 
  AppNotification,
  SupportTicket
} from '@/src/services/enhancedServices';
import { BusinessIdea, OptimizeBusiness } from '@/src/types';

interface EnhancedAppFeaturesProps {
  user: any;
  userProfile: any;
  isLoggedIn: boolean;
  onSignUpPrompt: () => void;
  savedVentures: BusinessIdea[];
  setSavedVentures: React.Dispatch<React.SetStateAction<BusinessIdea[]>>;
  onVentureClick: (venture: BusinessIdea) => void;
  setAppState: (state: any) => void;
  onSelectLanguageSimplified: (simplifiedOn: boolean) => void;
  isSimplifiedLanguageMode: boolean;
  isTechnicalSuiteOpen: boolean;
  setIsTechnicalSuiteOpen: (open: boolean) => void;
}

export const EnhancedAppFeatures: React.FC<EnhancedAppFeaturesProps> = ({
  user,
  userProfile,
  isLoggedIn,
  onSignUpPrompt,
  savedVentures,
  setSavedVentures,
  onVentureClick,
  setAppState,
  onSelectLanguageSimplified,
  isSimplifiedLanguageMode,
  isTechnicalSuiteOpen,
  setIsTechnicalSuiteOpen
}) => {
  // --- States ---
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isDemoActive, setIsDemoActive] = useState(false);

  const renderKeys = new Set<string>();
  const validateAndGetUniqueKey = (id: string | number, idx: number, prefix: string): string => {
    let key = `${prefix}-${id}-${idx}`;
    if (renderKeys.has(key)) {
      console.warn(`[Key Validation] Duplicate key detected in EnhancedAppFeatures: ${key}. Resolving...`);
      let suffix = 1;
      while (renderKeys.has(`${key}-${suffix}`)) {
        suffix++;
      }
      key = `${key}-${suffix}`;
    }
    renderKeys.add(key);
    return key;
  };
  
  // Offline-First status
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineSyncQueueCount, setOfflineSyncQueueCount] = useState(0);
  const [autoRetryActive, setAutoRetryActive] = useState(true);

  // Notifications
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [alertPrefs, setAlertPrefs] = useState<any>(null);

  // Support
  const [showSupportCorner, setShowSupportCorner] = useState(false);
  const [supportCategory, setSupportCategory] = useState("Scan Advice");
  const [supportText, setSupportText] = useState("");
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [supportChatMsg, setSupportChatMsg] = useState("");

  // Consent & Legal
  const [consent, setConsent] = useState<any>(null);
  const [showCookieCenter, setShowCookieCenter] = useState(false);
  const [showLegalToS, setShowLegalToS] = useState(false);
  const [showLegalPrivacy, setShowLegalPrivacy] = useState(false);

  // Experiment flags
  const [experimentFlags, setExperimentFlags] = useState<Record<string, string>>({});

  // Benchmarking & Matching
  const [activeTabBench, setActiveTabBench] = useState<'bench' | 'match' | 'success'>('bench');
  const [communityMatches, setCommunityMatches] = useState<any[]>([]);

  // Accessibility
  const [isSpeechTranslating, setIsSpeechTranslating] = useState(false);
  const [speechConfidence, setSpeechConfidence] = useState<number | null>(null);
  const [contrastActive, setContrastActive] = useState(false);
  const [ttsSpeed, setTtsSpeed] = useState(1);
  const [ttsPlaying, setTtsPlaying] = useState(false);

  // Sentinel Cost State
  const [sentinelStats, setSentinelStats] = useState<any>(null);

  // Control Enhancements Panel
  const isOpenPanel = isTechnicalSuiteOpen;
  const setIsOpenPanel = setIsTechnicalSuiteOpen;
  const [activePanelTab, setActivePanelTab] = useState<'support' | 'compliance' | 'ab' | 'network' | 'sentinel'>('compliance');

  // Load effects
  useEffect(() => {
    // Listen for network
    const handleOnline = () => {
      setIsOffline(false);
      InAppAlertEngine.pushNotification("Network Restored", "Sync connections reconnected smoothly. Commencing offline queue batching stream.", "MAINTENANCE", "low");
      // Process pending offline drafts and activities
      const actions = OfflineStorageManager.getQueue();
      if (actions.length > 0) {
        setOfflineSyncQueueCount(0);
        OfflineStorageManager.clearQueue();
        InAppAlertEngine.pushNotification("Sync Batch Finalized", `Synchronized ${actions.length} saved venture draft actions back up safely!`, "MAINTENANCE", "medium");
      }
    };
    const handleOffline = () => {
      setIsOffline(true);
      InAppAlertEngine.pushNotification("Operational Offline Engine Triggered", "Map tile local cache radius preserved (5KM boundary activated). Action logs are queued locally.", "MAINTENANCE", "critical");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check onboarding
    const onb = localStorage.getItem('envesta_onboarded_completed');
    if (!onb) {
      setShowOnboarding(true);
    }

    // Load initial helpers
    setNotifications(InAppAlertEngine.getNotifications());
    setAlertPrefs(InAppAlertEngine.getPreferences());
    setTickets(CustomerSupportEngine.getTickets(userProfile));
    setConsent(ComplianceConsentCenter.getConsent());
    setExperimentFlags(ExperimentEngine.assignVariant(userProfile?.name || 'Anonymous'));
    setCommunityMatches(FoundersDirectNetwork.getFounderMatches('Food Eateries'));
    setSentinelStats(CostAndCircuitBreaker.getStats());

    // Check offline items
    setOfflineSyncQueueCount(OfflineStorageManager.getQueue().length);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [userProfile]);

  // Demo loaders
  const loadEnterpriseDemoMode = () => {
    setIsDemoActive(true);
    // Push the demo kitchen venture to the active ventures
    setSavedVentures(prev => {
      const exists = prev.some(v => v.id === DEMO_MAMAS_KITCHEN_VENTURE.id);
      if (exists) return prev;
      return [DEMO_MAMAS_KITCHEN_VENTURE, ...prev];
    });

    InAppAlertEngine.pushNotification("Enterprise Demo Mode Active", "Sample watermarked data loaded. Explore 'Mama's Kitchen' metrics inside dynamic Hub modules.", "SUBSCRIPTION", "medium");
    setShowOnboarding(false);
    
    // Auto-navigate back to main feature
    setAppState((prev: any) => ({
      ...prev,
      isRevealed: false
    }));
  };

  const handleExportMyDataGDPR = () => {
    const dataReport = ComplianceConsentCenter.generateDataExportReport(user, userProfile, savedVentures);
    const blob = new Blob([dataReport], { type: 'application/json' });
    const u = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = u;
    link.download = `envesta_gdpr_data_export_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    InAppAlertEngine.pushNotification("Data Audit Pack Exported", "Complete JSON export compiled under ISO/GDPR standard requirements.", "SUBSCRIPTION", "low");
  };

  const handlePurgeMyAccount30D = () => {
    if (confirm("Are you absolutely certain you want to schedule complete account purging? Continuous files, saved ventures, and metadata will be permanently erased in 30 days under compliance parameters.")) {
      InAppAlertEngine.pushNotification("Compliance Account Purge Queued", "30-Day automated deletion pipeline activated. Safe harbor grace duration is editable under profile configurations.", "SUBSCRIPTION", "critical");
      alert("Purge scheduled safely. Your data is queued for destruction.");
    }
  };

  const handleCreateMockTicket = (e: React.FormEvent) => {
    e.preventDefault();
    if (!supportText.trim()) return;
    const ticket = CustomerSupportEngine.createTicket(userProfile, `Friction Query: ${supportCategory}`, supportCategory, supportText);
    setTickets(prev => [ticket, ...prev]);
    setSupportText("");
    InAppAlertEngine.pushNotification("Support Node Initiated", `Your ticket was routed to ${ticket.priority === 'critical' ? 'PRO High Escalation direct line' : 'Core support network'}.`, "MAINTENANCE", "low");
  };

  const triggerMockSpeechInput = () => {
    setIsSpeechTranslating(true);
    setSpeechConfidence(null);
    setTimeout(() => {
      setIsSpeechTranslating(false);
      setSpeechConfidence(98);
      // Simulate speaking " Kampala Central Organic Cafe with budget 10 million UGX"
      const docInput = document.getElementById('launchpad-sector') as HTMLInputElement;
      if (docInput) {
        docInput.value = "Organic Cafe";
        // Manual bubble trigger
        const ev = new Event('input', { bubbles: true });
        docInput.dispatchEvent(ev);
      }
      const locInput = document.getElementById('launchpad-location') as HTMLInputElement;
      if (locInput) {
        locInput.value = "Kampala Central";
        const ev = new Event('input', { bubbles: true });
        locInput.dispatchEvent(ev);
      }
      InAppAlertEngine.pushNotification("Voice Recognition Confirmed", "Parsed: 'Organic Cafe in Kampala Central' with high regional dialect accuracy (98%).", "MAINTENANCE", "low");
    }, 2000);
  };

  const speakFeasibilitySummary = () => {
    if (ttsPlaying) {
      setTtsPlaying(false);
      window.speechSynthesis.cancel();
      return;
    }
    const textToSpeak = "Welcome to eNvesta AI. You are looking at the Kampala Central strategic analysis dashboard. The high affinity sectors are food logistics, tech products, and last mile retail transportation hubs.";
    const u = new SpeechSynthesisUtterance(textToSpeak);
    u.rate = ttsSpeed;
    u.onend = () => setTtsPlaying(false);
    setTtsPlaying(true);
    window.speechSynthesis.speak(u);
  };

  const resetCircuitSentinel = () => {
    CostAndCircuitBreaker.resetBreaker();
    setSentinelStats(CostAndCircuitBreaker.getStats());
  };

  return (
    <>
      {/* FLOATING ACCESS ACCESSIBILITY & UTILITY BAR - Bottom Right */}
      <div className="fixed bottom-6 right-6 z-[200] flex items-center gap-3">
        {/* Connection Status Banner */}
        <div className={`flex items-center gap-2 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-lg border bg-transparent ${isOffline ? 'text-red-400 border-red-500/40' : 'text-emerald-400 border-emerald-500/40'}`}>
          {isOffline ? <WifiOff className="w-3.5 h-3.5" /> : <Wifi className="w-3.5 h-3.5" />}
          <span>{isOffline ? `OFFLINE (${offlineSyncQueueCount} PENDING)` : 'ONLINE'}</span>
        </div>

        {/* Simplified / Traditional Toggle */}
        <button 
          onClick={() => onSelectLanguageSimplified(!isSimplifiedLanguageMode)}
          className={`px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider border rounded-lg transition-all cursor-pointer bg-transparent hover:bg-transparent ${isSimplifiedLanguageMode ? 'text-[#F26522] border-[#F26522]' : 'text-white/70 border-white/[0.12] hover:text-white hover:border-white/30'}`}
          title="Toggle simplified plain vocabulary narration"
        >
          {isSimplifiedLanguageMode ? 'SIMPLIFIED ACTIVE' : 'STANDARD ENGLISH'}
        </button>
      </div>

      {/* --- ONBOARDING DIALOG WINDOW --- */}
      <AnimatePresence>
        {showOnboarding && (
          <div key="enhanced-onboarding-modal" className="fixed inset-0 z-[400] bg-black/80 flex items-center justify-center p-4">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#3a3430] max-w-2xl w-full p-8 md:p-10 border border-white/[0.06] rounded-xl text-white shadow-2xl"
            >
              <div className="flex justify-between items-start mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#F26522] text-white flex items-center justify-center font-bold rounded-lg text-sm">e</div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F26522]">eNvesta Intelligence Protocol</span>
                </div>
                <button onClick={() => setShowOnboarding(false)} className="text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5 p-1"><X className="w-5 h-5" /></button>
              </div>

              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white leading-none mb-2 font-heading">Onboarding Roadmap</h2>
                  <p className="text-xs font-normal text-white/60">Master dynamic localized valuation analysis in 60 seconds.</p>
                </div>

                {/* Progress dot checklist */}
                <div className="flex gap-2">
                  {[0, 1, 2, 3, 4].map((step) => (
                    <button 
                      key={step} 
                      onClick={() => setActiveStep(step)}
                      className={`h-1.5 flex-grow rounded-full transition-all cursor-pointer ${activeStep === step ? 'bg-[#F26522]' : 'bg-white/10 hover:bg-white/20'}`}
                    />
                  ))}
                </div>

                {/* Step content switches */}
                <div className="py-6 min-h-[220px] bg-[#322D29] border border-white/[0.06] rounded-xl p-6 space-y-4 text-left">
                  {activeStep === 0 && (
                    <div className="space-y-2">
                      <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#F26522] flex items-center gap-2">
                        <Rocket className="w-3.5 h-3.5" /> Step 01: Launchpad
                      </h4>
                      <p className="text-base font-bold uppercase font-heading leading-tight text-white">Validate with complete LaunchMath analytics</p>
                      <p className="text-xs font-normal leading-relaxed text-white/60">
                        Enter any raw business profile, locations index, target sectors and budget limit thresholds to project true margins, startup costs and real competitor profiles.
                      </p>
                    </div>
                  )}

                  {activeStep === 1 && (
                    <div className="space-y-2">
                      <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#F26522] flex items-center gap-2">
                        <Compass className="w-3.5 h-3.5" /> Step 02: Discovery Hub
                      </h4>
                      <p className="text-base font-bold uppercase font-heading leading-tight text-white">Map areas & locate gaps with live heatmaps</p>
                      <p className="text-xs font-normal leading-relaxed text-white/60">
                        Reverse the traditional process. Browse Kampala or global districts to reveal high density demand gaps, localized clusters, and real spotted physical entities.
                      </p>
                    </div>
                  )}

                  {activeStep === 2 && (
                    <div className="space-y-2">
                      <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#F26522] flex items-center gap-2">
                        <Gauge className="w-3.5 h-3.5" /> Step 03: eNvesta AI Optimize
                      </h4>
                      <p className="text-base font-bold uppercase font-heading leading-tight text-white">Scale and integrate real actuals</p>
                      <p className="text-xs font-normal leading-relaxed text-white/60">
                        Transition from theoretical planning to live operational monitoring. Connect spreadsheets to calibrate projected figures against actual overhead indexes.
                      </p>
                    </div>
                  )}

                  {activeStep === 3 && (
                    <div className="space-y-2">
                      <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#F26522] flex items-center gap-2">
                        <Shield className="w-3.5 h-3.5" /> Step 04: Scrutiny Deck
                      </h4>
                      <p className="text-base font-bold uppercase font-heading leading-tight text-white">Stress-test assumptions instantly</p>
                      <p className="text-xs font-normal leading-relaxed text-white/60">
                        Analyze capital dependencies, cold chain vulnerabilities, price volatility thresholds and regulatory barriers before spending valuable deployment funds.
                      </p>
                    </div>
                  )}

                  {activeStep === 4 && (
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#F26522] flex items-center gap-2">
                          <UserCheck className="w-3.5 h-3.5" /> Ready For Venture Intelligence
                        </h4>
                        <p className="text-base font-bold uppercase font-heading leading-tight text-white">Select your entry protocol below</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        <button 
                          onClick={loadEnterpriseDemoMode}
                          className="w-full bg-[#3a3430] hover:bg-white/10 text-white py-3.5 px-4 font-bold text-[10px] uppercase tracking-widest border border-white/[0.08] rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <Sparkles className="w-3.5 h-3.5 text-[#F26522]" />
                          Explore Sample Demo Room
                        </button>
                        <button 
                          onClick={() => {
                            localStorage.setItem('envesta_onboarded_completed', 'true');
                            setShowOnboarding(false);
                            onSignUpPrompt();
                          }}
                          className="w-full bg-[#F26522] hover:bg-[#F26522]/90 text-white py-3.5 px-4 font-bold text-[10px] uppercase tracking-widest rounded-lg transition-all cursor-pointer"
                        >
                          Sign Up (Individual / Company)
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer Controls */}
                <div className="flex justify-between items-center pt-4 border-t border-white/[0.06] text-[10px] font-bold uppercase tracking-widest text-white/60">
                  <button 
                    onClick={() => {
                      localStorage.setItem('envesta_onboarded_completed', 'true');
                      setShowOnboarding(false);
                      InAppAlertEngine.pushNotification("Anonymous Walkthrough Active", "Enjoy basic sandbox modules.", "SUBSCRIPTION", "low");
                    }}
                    className="text-white/40 hover:text-white transition-colors cursor-pointer"
                  >
                    Skip to Anonymous Sandbox
                  </button>

                  <div className="flex gap-4">
                    {activeStep > 0 && (
                      <button onClick={() => setActiveStep(prev => prev - 1)} className="hover:text-white transition-colors cursor-pointer">
                        Back
                      </button>
                    )}
                    {activeStep < 4 && (
                      <button onClick={() => setActiveStep(prev => prev + 1)} className="text-[#F26522] hover:text-[#F26522]/80 flex items-center gap-1 cursor-pointer font-bold">
                        Next <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* --- SIDE-PANEL INTEGRATED SUITE UTILITIES --- */}
      <AnimatePresence>
        {isOpenPanel && (
          <div key="enhanced-utilities-side-panel" className="fixed inset-y-0 right-0 z-[350] w-full max-w-xl bg-[#322D29] border-l border-white/[0.06] shadow-2xl flex flex-col pt-20 text-white" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <header className="p-6 sm:p-8 border-b border-white/[0.06] flex justify-between items-start bg-[#3a3430]">
              <div>
                <h3 className="text-xl font-bold uppercase text-white tracking-tight font-heading">eNvesta AI Dynamic Add-ons</h3>
                <p className="text-[10px] font-normal text-white/60 tracking-wider mt-1">Full Compliance, Support, Benchmarking, and Cost Metrics</p>
              </div>
              <button onClick={() => setIsOpenPanel(false)} className="text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5 p-1">
                <X className="w-5 h-5" />
              </button>
            </header>

            {/* Quick action tabs */}
            <div className="flex border-b border-white/[0.06] text-[10px] font-bold uppercase tracking-wider bg-[#322D29]">
              {[
                { id: 'compliance', label: 'Compliance & GDPR', icon: Shield },
                { id: 'support', label: 'Help Desk Direct', icon: Bot },
                { id: 'network', label: 'Founder Connect', icon: UserPlus },
                { id: 'ab', label: 'Experiments', icon: Settings },
                { id: 'sentinel', label: 'Spend & Circuit', icon: Activity }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActivePanelTab(tab.id as any)}
                  className={`flex-1 py-3.5 flex flex-col items-center gap-1 transition-all border-r border-white/[0.04] cursor-pointer ${activePanelTab === tab.id ? 'bg-[#3a3430] text-[#F26522] border-b-2 border-b-[#F26522]' : 'text-white/40 hover:text-white hover:bg-white/[0.02]'}`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  <span className="text-[8px] tracking-wider">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Content pane */}
            <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-8 bg-[#322D29] text-white custom-scrollbar">
              {/* COMPLIANCE TAB */}
              {activePanelTab === 'compliance' && (
                <div className="space-y-6 animate-in fade-in duration-300 text-left">
                  <div className="space-y-1.5">
                    <h4 className="text-base font-bold uppercase text-white font-heading">Regulatory Privacy Audit Console</h4>
                    <p className="text-xs text-white/60 font-normal leading-relaxed">
                      We offer full compliance coverage for our enterprise and institutional operators. Choose granular actions below.
                    </p>
                  </div>

                  <div className="p-5 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-4">
                    <h5 className="text-[10px] font-bold uppercase text-[#F26522] tracking-wider">GDPR Consent Control</h5>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-white">Essential Site cookies</span>
                      <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 border border-emerald-500/20 rounded uppercase">ALWAYS ACTIVE</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-white">Analytical Tracking</span>
                        <span className="text-[9px] text-white/40 uppercase">Tuned for ROI metrics telemetry</span>
                      </div>
                      <input 
                        type="checkbox" 
                        defaultChecked={consent?.analytics}
                        onChange={(e) => {
                          const next = { ...consent, analytics: e.target.checked };
                          ComplianceConsentCenter.saveConsent(next);
                          setConsent(next);
                        }}
                        className="w-4 h-4 accent-[#F26522] bg-[#322D29] border border-white/20 rounded cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Actions matrix */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button 
                      onClick={handleExportMyDataGDPR}
                      className="p-4 bg-[#3a3430] border border-white/[0.06] hover:border-[#F26522] text-left transition-all rounded-xl group flex items-start gap-3 cursor-pointer"
                    >
                      <Download className="w-4 h-4 text-[#F26522] mt-0.5 shrink-0" />
                      <div>
                        <span className="block text-xs font-bold uppercase text-white">Export GDPR Packet</span>
                        <span className="block text-[9px] text-white/50 uppercase mt-0.5">Structured JSON schema download</span>
                      </div>
                    </button>

                    <button 
                      onClick={handlePurgeMyAccount30D}
                      className="p-4 bg-red-500/10 border border-red-500/20 hover:border-red-500/40 text-left transition-all rounded-xl flex items-start gap-3 cursor-pointer"
                    >
                      <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="block text-xs font-bold uppercase text-red-400">Schedule Deletion</span>
                        <span className="block text-[9px] text-red-300/60 uppercase mt-0.5">Standard 30D grace wipe period</span>
                      </div>
                    </button>
                  </div>

                  {/* PDFs and Legals */}
                  <div className="grid grid-cols-3 gap-2 text-center text-[9px] font-bold uppercase tracking-wider text-white/60">
                    <button onClick={() => setShowLegalToS(true)} className="py-2.5 bg-[#3a3430] hover:bg-[#F26522] hover:text-white border border-white/[0.06] rounded-lg transition-all cursor-pointer">ToS Agreement</button>
                    <button onClick={() => setShowLegalPrivacy(true)} className="py-2.5 bg-[#3a3430] hover:bg-[#F26522] hover:text-white border border-white/[0.06] rounded-lg transition-all cursor-pointer">Privacy Policy</button>
                    <button className="py-2.5 bg-[#3a3430] hover:bg-[#F26522] hover:text-white border border-white/[0.06] rounded-lg transition-all cursor-pointer">DPA Addendum</button>
                  </div>
                </div>
              )}

              {/* SUPPORT TAB */}
              {activePanelTab === 'support' && (
                <div className="space-y-6 animate-in fade-in duration-300 text-left">
                  <div className="space-y-1">
                    <h4 className="text-base font-bold uppercase text-white font-heading">Help Desk Triage Center</h4>
                    <p className="text-xs text-white/60 font-normal">Automated AI assistance and Direct Developer Routing.</p>
                  </div>

                  {/* Interactive chat section */}
                  <div className="border border-white/[0.06] bg-[#3a3430] rounded-xl p-4 space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-white/[0.06]">
                      <Bot className="w-4 h-4 text-[#F26522]" />
                      <span className="text-[9px] font-bold uppercase tracking-widest text-white/50">Triage Agent Stream Offline</span>
                    </div>

                    <div className="max-h-40 overflow-y-auto space-y-3 p-1">
                      <div className="bg-[#322D29] text-white/90 p-3 text-xs font-normal rounded-lg max-w-[85%] border border-white/[0.06]">
                        Hello. I'm currently monitoring your eNvesta AI active features. Need specialized help with your regional Kampala feasibility parameters? Speak your criteria.
                      </div>
                      {supportChatMsg && (
                        <div className="bg-[#F26522] text-white p-3 text-xs font-medium rounded-lg max-w-[85%] self-end ml-auto">
                          {supportChatMsg}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        placeholder="INPUT SUPPORT QUERY..."
                        value={supportChatMsg}
                        onChange={(e) => setSupportChatMsg(e.target.value)}
                        className="flex-1 bg-[#322D29] border border-white/10 rounded-lg text-xs font-normal text-white placeholder-white/40 px-3 py-2 outline-none focus:border-[#F26522]"
                      />
                      <button 
                        onClick={() => {
                          if (!supportChatMsg.trim()) return;
                          InAppAlertEngine.pushNotification("Query Queued", "Developer tickets submitted from direct dashboard parameters.", "MAINTENANCE", "low");
                        }}
                        className="bg-[#F26522] hover:bg-[#F26522]/90 text-white px-4 py-2 font-bold text-xs uppercase tracking-wider rounded-lg transition-all font-heading cursor-pointer"
                      >
                        Ask
                      </button>
                    </div>
                  </div>

                  {/* Support form */}
                  <form onSubmit={handleCreateMockTicket} className="space-y-4 border-t border-white/[0.06] pt-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[8px] font-bold uppercase tracking-widest text-white/40 mb-1.5">Hub Query Domain</label>
                        <select 
                          value={supportCategory}
                          onChange={(e) => setSupportCategory(e.target.value)}
                          className="w-full bg-[#322D29] border border-white/10 rounded-lg text-xs font-medium text-white p-2.5 outline-none focus:border-[#F26522]"
                        >
                          <option value="Scan Advice">Launchpad / Scan Advice</option>
                          <option value="Billing Sync">Premium Subscription billing</option>
                          <option value="Mapping Gaps">Discovery Heatmaps calibration</option>
                          <option value="Excel API">Optimize Excel spreadsheets connect</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[8px] font-bold uppercase tracking-widest text-white/40 mb-1.5">Direct Developer Ticket</label>
                        <button 
                          type="submit"
                          className="w-full py-2.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-xs font-bold uppercase tracking-widest rounded-lg transition-all cursor-pointer"
                        >
                          Submit Core Ticket
                        </button>
                      </div>
                    </div>

                    <div>
                      <textarea 
                        rows={3}
                        placeholder="PROVIDE ADDITIONAL CASE METRIC DETAILS..."
                        value={supportText}
                        onChange={(e) => setSupportText(e.target.value)}
                        className="w-full bg-[#322D29] border border-white/10 rounded-lg p-3 text-xs text-white placeholder-white/40 outline-none focus:border-[#F26522]"
                      />
                    </div>
                  </form>

                  {/* Ticket list */}
                  <div className="space-y-3">
                    <h5 className="text-[9px] font-bold uppercase tracking-widest text-white/40">Historic Support Incident logs</h5>
                    {tickets.map(t => (
                      <div key={t.id} className="p-4 bg-[#3a3430] border border-white/[0.06] rounded-lg flex items-center justify-between text-xs font-medium">
                        <div>
                          <span className="block text-white text-xs font-bold">{t.subject}</span>
                          <span className="block text-[8px] text-white/50 mt-1">{t.category} • priority: <strong className="text-[#F26522]">{t.priority}</strong></span>
                        </div>
                        <span className={`px-2 py-0.5 text-[8px] font-bold uppercase rounded ${t.status === 'open' ? 'bg-[#F26522]/10 text-[#F26522] border border-[#F26522]/20' : 'bg-white/5 text-white/60 border border-white/10'}`}>
                          {t.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* NETWORK CONNECTIONS */}
              {activePanelTab === 'network' && (
                <div className="space-y-6 animate-in fade-in duration-300 text-left">
                  <div className="space-y-1">
                    <h4 className="text-base font-bold uppercase text-white font-heading">Collective Founder Network</h4>
                    <p className="text-xs text-white/60 font-normal">Opt-In Matching with Non-Competing Co-Operators</p>
                  </div>

                  {/* Benchmark */}
                  <div className="p-5 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-3">
                    <span className="text-[9px] font-bold uppercase text-[#F26522] tracking-wider">Dynamic Benchmarking Node</span>
                    <h5 className="text-sm font-bold uppercase text-white font-heading">Kampala Central Eatery Trends</h5>
                    <div className="grid grid-cols-2 gap-4 text-center py-3 bg-[#322D29] border border-white/[0.06] rounded-lg">
                      <div>
                        <span className="block text-2xl font-bold text-[#F26522]">78th</span>
                        <span className="block text-[8px] font-bold uppercase tracking-widest text-white/50">Venture percentile</span>
                      </div>
                      <div>
                        <span className="block text-2xl font-bold text-white">18</span>
                        <span className="block text-[8px] font-bold uppercase tracking-widest text-white/50">Verified peer samples</span>
                      </div>
                    </div>
                    <ul className="space-y-1.5 text-xs text-white/70 list-disc pl-4 leading-relaxed font-normal">
                      <li>Digital catering lunch models drop overhead index thresholds by 28%.</li>
                      <li>Produce price hedges reduce inflationary volatility impacts.</li>
                    </ul>
                  </div>

                  {/* Matches list */}
                  <div className="space-y-4">
                    <h5 className="text-[10px] font-bold uppercase text-white/40">Matched peer founders in same sector:</h5>
                    {communityMatches.map((p, idx) => {
                      const matchUniqueKey = validateAndGetUniqueKey(p.name || 'match', idx, 'community-match');
                      return (
                      <div key={matchUniqueKey} className="p-5 bg-[#3a3430] border border-white/[0.06] hover:border-[#F26522]/40 rounded-xl transition-all space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-bold uppercase text-white">{p.name}</span>
                          <span className="px-2.5 py-0.5 bg-emerald-500/10 text-emerald-400 text-[8px] font-bold uppercase border border-emerald-500/20 rounded">{p.status}</span>
                        </div>
                        <p className="text-[9px] text-white/50 font-bold uppercase tracking-wider">{p.location} • {p.sector}</p>
                        <p className="text-xs text-white/70 leading-relaxed font-normal">{p.matchReason}</p>
                        <button 
                          onClick={() => {
                            InAppAlertEngine.pushNotification("Match Request Dispatched", `Secure introductions sent to ${p.name} safely.`, "TEAM_MENTION", "low");
                          }}
                          className="py-2 px-4 bg-[#322D29] hover:bg-[#F26522] border border-white/10 hover:border-[#F26522] text-white font-bold text-[9px] uppercase tracking-widest rounded-lg transition-all cursor-pointer"
                        >
                          Request intro handshake
                        </button>
                      </div>
                    );
                    })}
                  </div>
                </div>
              )}

              {/* EXPERIMENTS A/B CONTROL */}
              {activePanelTab === 'ab' && (
                <div className="space-y-6 animate-in fade-in duration-300 text-left">
                  <div className="space-y-1">
                    <h4 className="text-base font-bold uppercase text-white font-heading">Experiment Configuration Matrix</h4>
                    <p className="text-xs text-white/60 font-normal">Calibrate rolling variant deployments</p>
                  </div>

                  <div className="p-4 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-2">
                    <p className="text-xs text-white/70 font-normal leading-relaxed">
                      This framework allocates features to buckets based on percentage user telemetry logs to test conversion velocity.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h5 className="text-[9px] font-bold uppercase tracking-widest text-white/40">Active percentage rolls</h5>
                    {Object.entries(experimentFlags).map(([flag, variant]) => (
                      <div key={flag} className="p-4 bg-[#3a3430] border border-white/[0.06] rounded-lg flex items-center justify-between text-xs">
                        <div>
                          <span className="block font-bold text-white capitalize">{flag.replace(/_/g, ' ')}</span>
                          <span className="block text-[8px] text-white/40 mt-0.5">Config telemetry flags</span>
                        </div>
                        <span className="px-3 py-1 bg-[#322D29] border border-white/10 text-white font-bold text-[9px] uppercase tracking-wider rounded">
                          {variant.toUpperCase()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SENTINEL SPEND BUDGETS */}
              {activePanelTab === 'sentinel' && (
                <div className="space-y-6 animate-in fade-in duration-300 text-left">
                  <div className="space-y-1">
                    <h4 className="text-base font-bold uppercase text-white font-heading">AI Sentinel Cost Protection</h4>
                    <p className="text-xs text-white/60 font-normal">Circuit Breakers, Burn Metrics, & Auto Fallback</p>
                  </div>

                  {sentinelStats && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-[#3a3430] border border-white/[0.06] rounded-xl text-center">
                          <span className="block text-2xl font-bold text-white">${sentinelStats.totalCostUSD.toFixed(3)}</span>
                          <span className="block text-[8px] font-bold uppercase text-white/40 tracking-widest mt-1">Accrued AI expense</span>
                        </div>
                        <div className="p-4 bg-[#3a3430] border border-white/[0.06] rounded-xl text-center">
                          <span className="block text-2xl font-bold text-white">${sentinelStats.budgetCapUSD.toFixed(2)}</span>
                          <span className="block text-[8px] font-bold uppercase text-white/40 tracking-widest mt-1">Daily Stop Threshold</span>
                        </div>
                      </div>

                      <div className="p-5 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-4">
                        <span className="text-[9px] font-bold uppercase text-[#F26522] tracking-widest block">Circuit Breaker diagnostics</span>
                        <div className="flex justify-between items-center text-xs font-medium">
                          <span>Breaker State</span>
                          <span className={`px-2.5 py-0.5 rounded text-[9px] font-bold ${sentinelStats.breakerTripped ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
                            {sentinelStats.breakerTripped ? 'TRIPPED (SERVE LOCAL TEMPLATES)' : 'CLOSED (HEALTHY)'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-xs font-medium pt-2 border-t border-white/[0.04]">
                          <span className="text-white/60">Active Fallback model</span>
                          <span className="text-white tracking-wider font-mono text-[11px]">{sentinelStats.currentModel.toUpperCase()}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs font-medium pt-2 border-t border-white/[0.04]">
                          <span className="text-white/60">Consecutive API Latency failures</span>
                          <span className="text-white font-mono text-[11px]">{sentinelStats.consecutiveFailures} / 5 LIMIT</span>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button 
                          onClick={resetCircuitSentinel}
                          className="flex-1 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold text-[9px] uppercase tracking-widest py-3 rounded-lg transition-colors cursor-pointer"
                        >
                          Manual reset breaker node
                        </button>
                        <button 
                          onClick={() => {
                            InAppAlertEngine.pushNotification("Model Fallback Triggered", "Model fallback: switched from Gemini 3.5 Pro to Gemini 3.5 Flash for optimal operational cost.", "MAINTENANCE", "low");
                          }}
                          className="flex-1 border border-white/10 hover:border-[#F26522] bg-[#3a3430] text-white font-bold text-[9px] uppercase tracking-widest py-3 rounded-lg transition-colors cursor-pointer"
                        >
                          Force Flash model selection
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* --- POPUP WINDOW MODULES INPORTED --- */}
      <AnimatePresence>
        {showLegalToS && (
          <div key="enhanced-legal-tos-modal" className="fixed inset-0 z-[500] bg-black/80 flex items-center justify-center p-6">
            <div className="bg-[#3a3430] max-w-xl w-full p-8 border border-white/[0.06] rounded-xl shadow-2xl flex flex-col max-h-[80vh] text-white">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-lg font-bold uppercase tracking-tight text-white font-heading">Terms of Service</h3>
                <button onClick={() => setShowLegalToS(false)} className="text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5 p-1"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto space-y-4 text-xs font-normal text-white/70 leading-relaxed font-dm bg-[#322D29] p-6 border border-white/[0.06] rounded-xl custom-scrollbar text-left">
                <p><strong className="text-white">1. Acceptable Use</strong></p>
                <p>eNvesta AI services provides hyper-localized valuation index parameters. Results are based on AI heuristic models and public census indices.</p>
                <p><strong className="text-white">2. Limitation of Liability</strong></p>
                <p>Under no circumstances shall eNvesta AI bear corporate liability for physical real estate site deployments or raw asset capital allocations. Users assume complete entrepreneurial risks.</p>
                <p><strong className="text-white">3. Dispute resolution</strong></p>
                <p>Governed strictly under standard Kampala Commercial Arbitration Act parameters.</p>
              </div>
            </div>
          </div>
        )}

        {showLegalPrivacy && (
          <div key="enhanced-legal-privacy-modal" className="fixed inset-0 z-[500] bg-black/80 flex items-center justify-center p-6">
            <div className="bg-[#3a3430] max-w-xl w-full p-8 border border-white/[0.06] rounded-xl shadow-2xl flex flex-col max-h-[80vh] text-white">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-lg font-bold uppercase tracking-tight text-white font-heading">Privacy Policy</h3>
                <button onClick={() => setShowLegalPrivacy(false)} className="text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5 p-1"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto space-y-4 text-xs font-normal text-white/70 leading-relaxed font-dm bg-[#322D29] p-6 border border-white/[0.06] rounded-xl custom-scrollbar text-left">
                <p><strong className="text-white">1. Data Collection Scope</strong></p>
                <p>We process geolocations telemetry, scanned target sectors, financial budget indexes, active POS spreadsheet attachments, cookies preference logs.</p>
                <p><strong className="text-white">2. Right of Access & Deletion</strong></p>
                <p>Users hold immediate authority to export full archives (GDPR Packet) or execute absolute account deletion (grace period 30D purge rule).</p>
                <p><strong className="text-white">3. Third Party Sharing</strong></p>
                <p>Integrations such as Firebase Firestore, Firebase Authentication, and Google Maps API follow secure transit architectures; zero public sharing.</p>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
